// src/contexts/GoalDashboardContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { goalSettingService } from "../service/goal-setting.service";
import { GoalItem } from "../models/GoalSettingDashboard";
import { AvailableSubOrganization, GoalLock } from "../models/GoalSetting";
import { useNavigate } from "react-router-dom";
import alertService from "../components/alert/alertService";
import { Trans, useTranslation } from "react-i18next";
import { common } from "../i18n";
import { promptsForEmissions, useGoalLimit } from "./useGoalLimit";

interface GoalDashboardState {
  allGoals: GoalItem[];
  selectedGoalIds: string[];
  setSelectedGoalIds: (ids: string[]) => void;
  displayedGoals: GoalItem[];
  setDisplayedGoals: (goals: GoalItem[]) => void;
  goalLock: GoalLock;
}

const GoalDashboardContext = createContext<GoalDashboardState | null>(null);

export const GoalDashboardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [allGoals, setAllGoals] = useState<GoalItem[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [displayedGoals, setDisplayedGoals] = useState<GoalItem[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [isGoalListLoaded, setIsGoalListLoaded] = useState(false);
  const goalLock = useGoalLimit(isGoalListLoaded ? allGoals.length : undefined);
  const isPlanKnown = goalLock.state !== "loading";
  const [hasEmission, setHasEmission] = useState<boolean | undefined>();

  const { t } = useTranslation([common], { nsMode: "fallback" });

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      let result;
      try {
        result = await goalSettingService.getAvailableSuborganizationList();
      } catch {
        // error alert already shown by service
        return;
      }
      if (!result) return;

      let res;
      try {
        res = await goalSettingService.getDashboard();
      } catch {
        // error alert already shown by service
        return;
      } finally {
        setIsGoalListLoaded(true);
      }
      setAllGoals(res?.goals || []);
      setHasEmission(result.suborganizations.some((s) => s.hasEmission));

      // ✅ ลบออก ไม่ต้องใช้ isReady แล้ว
    };

    init();
  }, []);

  // the plan and the goals load together; the prompt waits for both
  useEffect(() => {
    if (hasEmission === undefined || !isPlanKnown) return;
    if (!promptsForEmissions(hasEmission, goalLock)) return;
    const prompt = async () => {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );

      await alertService.confirm(
        t("Historical-Data-Not-Found"),
        <Trans
          t={t}
          i18nKey="Historical-Data-At-Least-Description"
          components={{ span: <span style={{ color: "var(--red)" }} /> }}
        />,
        t("Add-Emission"),
        "var(--dark)",
        true,
        undefined,
        undefined,
        undefined,
      );
      navigate("/emission/add");
    };

    prompt();
  }, [hasEmission, isPlanKnown]);

  useEffect(() => {
    const selected = allGoals.filter((g) =>
      selectedGoalIds.includes(g.goalRefId),
    );
    setDisplayedGoals(selected);
  }, [selectedGoalIds, allGoals]);

  // ✅ ยังไม่ ready → ไม่ render children (กันกระพริบหรือ race condition)
  // if (!isReady) return null;

  return (
    <GoalDashboardContext.Provider
      value={{
        allGoals,
        selectedGoalIds,
        setSelectedGoalIds,
        displayedGoals,
        setDisplayedGoals,
        goalLock,
      }}
    >
      {children}
    </GoalDashboardContext.Provider>
  );
};

export const useGoalDashboardContext = () => {
  const ctx = useContext(GoalDashboardContext);
  if (!ctx)
    throw new Error(
      "useGoalDashboardContext must be used within GoalDashboardProvider",
    );
  return ctx;
};
